import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { UserRole } from '../types';

interface AdminPanelProps {
  onClose: () => void;
  userRole: UserRole;
  onToggleRole: () => void;
  getRoleDisplayName: (role: UserRole) => string;
  getRoleIcon: (role: UserRole) => string;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  onClose, 
  userRole, 
  onToggleRole, 
  getRoleDisplayName, 
  getRoleIcon 
}) => {
  const { state, updateTableCount, updateTableNumber, resetAllTables } = useApp();
  const [newTableCount, setNewTableCount] = useState(state.totalTables);
  const [editingTableNumbers, setEditingTableNumbers] = useState(false);
  const [tableNumbers, setTableNumbers] = useState<{[key: number]: string}>(
    state.tables.reduce((acc, table) => {
      acc[table.id] = table.number;
      return acc;
    }, {} as {[key: number]: string})
  );

  const handleTableCountChange = (count: number) => {
    if (count >= 1 && count <= 100) {
      setNewTableCount(count);
      updateTableCount(count);
    }
  };

  const handleTableNumberChange = (tableId: number, newNumber: string) => {
    setTableNumbers(prev => ({
      ...prev,
      [tableId]: newNumber
    }));
  };

  const saveTableNumbers = () => {
    Object.entries(tableNumbers).forEach(([id, number]) => {
      updateTableNumber(parseInt(id), number);
    });
    setEditingTableNumbers(false);
  };

  const resetToDefault = () => {
    const defaultNumbers = state.tables.reduce((acc, table) => {
      acc[table.id] = `#${table.id}`;
      return acc;
    }, {} as {[key: number]: string});
    
    setTableNumbers(defaultNumbers);
    Object.entries(defaultNumbers).forEach(([id, number]) => {
      updateTableNumber(parseInt(id), number);
    });
  };

  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="admin-header">
        <h3 className="admin-title">Einstellungen</h3>
        <button className="admin-close" onClick={onClose}>×</button>
      </div>

      <div className="admin-content">
        {/* Benutzer-Rolle Sektion */}
        <div className="admin-section">
          <div className="section-header">
            <h4 className="section-title">Benutzer-Rolle</h4>
          </div>
          <div className="role-management">
            <div className="current-role">
              <span className="role-display">
                <span className="role-icon">{getRoleIcon(userRole)}</span>
                <span className="role-name">Aktuelle Rolle: {getRoleDisplayName(userRole)}</span>
              </span>
            </div>
            <button 
              className="role-toggle-btn"
              onClick={onToggleRole}
              title="Rolle wechseln"
            >
              <span>🔄</span>
              <span>Rolle wechseln</span>
            </button>
          </div>
        </div>

        {/* Tischanzahl Sektion */}
        <div className="admin-section">
          <div className="section-header">
            <h4 className="section-title">Tischanzahl konfigurieren</h4>
            <span className="section-subtitle">Aktuelle Anzahl: {newTableCount}</span>
          </div>
          
          <div className="counter-control">
            <button
              className="counter-btn counter-minus"
              onClick={() => handleTableCountChange(newTableCount - 1)}
              disabled={newTableCount <= 1}
            >
              -
            </button>
            <input
              type="number"
              className="counter-input"
              value={newTableCount}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value)) {
                  handleTableCountChange(value);
                }
              }}
              min="1"
              max="100"
            />
            <button
              className="counter-btn counter-plus"
              onClick={() => handleTableCountChange(newTableCount + 1)}
              disabled={newTableCount >= 100}
            >
              +
            </button>

            <div className="quick-select">
              <span className="quick-select-label">Häufige Werte:</span>
              <div className="quick-select-buttons">
                {[10, 16, 20, 25, 30, 36].map(count => (
                  <button
                    key={count}
                    className={`quick-btn ${count === newTableCount ? 'active' : ''}`}
                    onClick={() => handleTableCountChange(count)}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tischnummern Sektion */}
        <div className="admin-section">
          <div className="section-header">
            <h4 className="section-title">Tischnummern verwalten</h4>
            <button
              className={`edit-toggle ${editingTableNumbers ? 'active' : ''}`}
              onClick={() => setEditingTableNumbers(!editingTableNumbers)}
            >
              {editingTableNumbers ? 'Speichern' : 'Bearbeiten'}
            </button>
          </div>
          
          {editingTableNumbers && (
            <div className="table-numbers-editor">
              <div className="table-numbers-grid">
                {state.tables.map(table => (
                  <div key={table.id} className="table-number-item">
                    <label className="table-id-label">#{table.id}</label>
                    <input
                      type="text"
                      className="table-number-input"
                      value={tableNumbers[table.id] || table.number}
                      onChange={(e) => handleTableNumberChange(table.id, e.target.value)}
                      placeholder={`#${table.id}`}
                    />
                  </div>
                ))}
              </div>
              
              <div className="table-actions">
                <button className="save-btn" onClick={saveTableNumbers}>
                  Speichern
                </button>
                <button className="reset-btn" onClick={resetToDefault}>
                  Standard wiederherstellen
                </button>
              </div>
            </div>
          )}
        </div>

        {/* App Status Sektion */}
        <div className="admin-section">
          <div className="section-header">
            <h4 className="section-title">System Status</h4>
          </div>
          <div className="status-grid">
            <div className="status-item">
              <span className="status-label">Aktive Tische</span>
              <span className="status-value">{state.tables.filter(t => t.status !== 'available').length}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Gesamt Tische</span>
              <span className="status-value">{state.tables.length}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Letzte Sync</span>
              <span className="status-value">{new Date(state.lastSync).toLocaleTimeString()}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Version</span>
              <span className="status-value">{state.version}</span>
            </div>
          </div>
        </div>

        {/* Reset Sektion */}
        <div className="admin-section danger-section">
          <button
            className="reset-all-btn"
            onClick={() => {
              if (window.confirm('Alle Tische zurücksetzen?\n\nDies setzt alle Tische auf "frei" und kann nicht rückgängig gemacht werden!')) {
                resetAllTables();
                console.log('✅ Alle Tische wurden zurückgesetzt');
              }
            }}
          >
            Alle Tische zurücksetzen
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;